'use client'
import React, { useEffect, useState } from 'react';
// import { title } from "@/app/[lang]/components/primitives";
import { Textarea, Button, Switch } from "@nextui-org/react";
// import { Locale } from '@/i18n-config';

import type { Article } from '@/types';
import { notFound } from 'next/navigation';
import edjsHTML from 'editorjs-html';


interface ArticleViewPageProps {
	params: {
		lang: string;
	};
}

export default function CorruptionPage(
	{ params }: ArticleViewPageProps
) {
	const { lang } = params;
	const [agreed, setAgreed] = useState(false);
	const [article, setArticle] = useState<Article | null>(null);
	const [loading, setLoading] = useState(true);
	
	// eslint-disable-next-line no-unused-vars
	const [error, setError] = useState<string | null>(null);


	const fetchArticle = async () => {
		try {
			setLoading(true);
			setError(null);

			const response = await fetch(`/api/articles/by-slug/corruption-report?lang=${lang}&public=true`);

			if (response.status === 404) {
				notFound();
				return;
			}

			if (!response.ok) {
				throw new Error('Failed to fetch article');
			}

			const articleData = await response.json();

			// Check if article is published and public
			if (!articleData.published || articleData.state !== 'PUBLISHED') {
				notFound();
				return;
			}

			setArticle(articleData);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch article');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchArticle();
	}, [lang]);

	if (loading) {
		return (
			<div className="min-h-screen bg-white">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
					<div className="animate-pulse">
						<div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
						<div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
						<div className="h-64 bg-gray-200 rounded mb-8"></div>
						<div className="space-y-4">
							<div className="h-4 bg-gray-200 rounded"></div>
							<div className="h-4 bg-gray-200 rounded w-5/6"></div>
							<div className="h-4 bg-gray-200 rounded w-4/6"></div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const generateTableOfContents = () => {
		if (!article?.translations) return [];
		const translation = article.translations.find(t => t.lang === lang) || article.translations[0];
		if (!translation?.blocks) return [];
		const toc: Array<{ id: string; text: string; level: number }> = [];
		translation.blocks.forEach((block: any) => {
			if (block.htmlContent) {
				const parser = new DOMParser();
				const doc = parser.parseFromString(block.htmlContent, 'text/html');
				const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
				headings.forEach((heading) => {
					const id = heading.id || heading.textContent?.toLowerCase().replace(/\s+/g, '-') || '';
					const level = parseInt(heading.tagName.substring(1));
					if (id && heading.textContent) {
						toc.push({ id, text: heading.textContent, level });
					}
				});
			}
		});
		return toc;
	};

	const renderArticleContent = () => {
		if (!article?.translations) return null;
		const translation = article.translations.find(t => t.lang === lang) || article.translations[0];
		if (!translation?.blocks) return null;
		const contentBlocks = translation.blocks
			.filter((block: any) => block.show && block.blockType === 'CONTENT')
			.sort((a: any, b: any) => a.sortOrder - b.sortOrder);

		return contentBlocks.map((block: any) => {
			if (block.htmlContent) {
				return (
					<div
						key={block.id}
						className={`prose prose-lg max-w-none ${block.cssClass || ''}`}
						style={block.style}
						dangerouslySetInnerHTML={{ __html: block.htmlContent }}
					/>
				);
			}
			if (block.value && typeof block.value === 'object' && block.value.blocks) {
				return (
					<div
						key={block.id}
						className={`prose prose-lg max-w-none ${block.cssClass || ''}`}
						style={block.style}
					>
						{renderEditorJSBlocks(block.value.blocks)}
					</div>
				);
			}
			if (block.plainText) {
				return (
					<div
						key={block.id}
						className={`prose prose-lg max-w-none ${block.cssClass || ''}`}
						style={block.style}
					>
						<p>{block.plainText}</p>
					</div>
				);
			}
			return null;
		});
	};

	const renderArticleTitle = () => {
		if (!article?.translations) return null;
		const translation = article.translations.find(t => t.lang === lang) || article.translations[0];
		return translation?.title || article.title;		
	};

	const renderEditorJSBlocks = (blocks: any[]): React.ReactNode => {
		const edjsParser = edjsHTML({
			table: (block: any) => {
				if (!block.data || !block.data.content) return '';
				const rows = block.data.content
					.map(
						(row: string[], rowIndex: number) =>
							`<tr>${row
								.map(
									(cell: string) =>
										rowIndex === 0
											? `<th class="px-4 py-2 bg-gray-100 text-left font-semibold border-b border-gray-200">${cell}</th>`
											: `<td class="px-4 py-2 border-b border-gray-100">${cell}</td>`
								)
								.join('')}</tr>`
					)
					.join('');
				return `<table class="editorjs-table">${rows}</table>`;
			}
		});

		return blocks.map((block, index) => {
			try {
				// Convert single block to HTML
				const html = edjsParser.parseBlock(block);

				if (html) {
					return (
						<div
							key={`block-${index}`}
							dangerouslySetInnerHTML={{ __html: String(html) }}
						/>
					);
				}

				// Fallback for unsupported block types
				return (
					<div key={`block-${index}`} className="my-4 p-4 bg-gray-50 rounded-md">
						<p className="text-sm text-gray-600">
							Unsupported block type: {block.type}
						</p>
					</div>
				);
			} catch (error) {
				console.error('Error rendering EditorJS block:', error, block);
				return (
					<div key={`block-${index}`} className="my-4 p-4 bg-red-50 rounded-md">
						<p className="text-sm text-red-600">
							Error rendering block
						</p>
					</div>
				);
			}
		});
	};


	// eslint-disable-next-line no-unused-vars
	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString(lang, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};


	// eslint-disable-next-line no-unused-vars
	const tableOfContents = generateTableOfContents();

	return (
		<div className="isolate px-2 py-6 xl:px-32 2xl:px-64 min-h-screen">
			<div
				aria-hidden="true"
				className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
			>
				<div
					style={{
						clipPath:
							'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
					}}
					className="relative left-1/2 -z-10 aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-40rem)] sm:w-[72.1875rem]"
				/>
			</div>

			<div className="max-w-7xl mx-auto bg-white dark:bg-zinc-900 dark:text-gray-100 p-8 rounded-lg shadow-md transition-colors">
				<div className="mx-auto text-center py-8 dark:text-gray-300 ">
					<h2 className="text-balance text-4xl font-semibold tracking-tight sm:text-4xl up">{renderArticleTitle()}</h2>
					{/* <p className="mt-2 text-lg/8 max-w-xl">Payment agency. Lorem ipsum, or lipsum as it is sometimes known, is dummy text used in laying out print, graphic or web designs.</p> */}
				</div>

				<form action="#" method="POST" className="mx-auto mt-16 w-full max-w-3xl sm:mt-20">
					<div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">

						<div className='sm:col-span-2'>
							<Textarea
								disableAutosize
								variant="bordered"
								type="multiline"
								name='message'
								label="Message"
								isRequired
								placeholder="Enter your message"
								classNames={{
									input: "resize-y min-h-[40px]",
								}}
							/>
						</div>

						<div className="flex gap-x-4 sm:col-span-2">

							<div className="sm:col-span-2">
								<Switch isSelected={agreed} onValueChange={setAgreed} size="sm">
									By selecting this, you agree to our{' '}
									<a href="#" className="font-semibold text-indigo-600">
										privacy&nbsp;policy
									</a>
									.
								</Switch>
							</div>

						</div>

					</div>
					<div className="mt-10">
						<Button
							type="submit"
							className="block w-full"
							color='warning'

						>
							Send a report about corruption
						</Button>
					</div>
				</form>

				<div className="mt-10">
					<div className="w-full max-w-5xl mx-auto">
						<article className="max-w-none prose prose-lg dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-gray-800 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-300 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-hr:border-gray-200 dark:prose-hr:border-gray-700">
							{renderArticleContent()}
						</article>
					</div>
				</div>
			</div>
		</div>
	);
}


